import { useEffect, useRef, useState } from "react";
import { Card, Button, Form, Row, Col, InputGroup } from "react-bootstrap";

import {
  ExternalLink,
  Copy,
  Plus,
  RefreshCw,
  Download,
  Edit,
  Check,
  RefreshCwIcon,
  Link,
} from "lucide-react";
import { BACKEND_URL, FRONTEND_URL } from "../config";
import { PLACE_HOLDER_IMAGE } from "../constants";
import Loader from "./Loader";
import { message } from "antd";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/free-solid-svg-icons";
import EditUrlModal from "./EditUrlModal";
import { useSelector } from "react-redux";

const flowTypes = [
  { value: "standard", label: "Standard Flow" },
  { value: "direct", label: "Direct Flow" },
  { value: "check_in", label: "Check-in Flow" },
  { value: "multiple_check_in", label: "Multiple Check-in Flow" },
];

const QrCodeDetail = ({ serviceConfig = {}, openQRCodeModal }) => {
  const [editing, setEditing] = useState(false);
  const [urlEditModal, setUrlEditModal] = useState(false);
  const [newUrl, setNewUrl] = useState("");

  const { selectedUser: client } = useSelector((state) => state.client);

  // Show "direct" only if client.model.b2b is true
  const filteredFlowTypes = client?.model?.b2b
    ? flowTypes
    : flowTypes.filter((f) => f.value !== "direct");

  const fileInputRef = useRef(null);

  const {
    title = "Service",
    flow = "",
    feeOption = false,
    qrCode = "",
    serviceKey,
    botRef = "",
    onChange,
    onUpload,
    uploading = false,
    onSave,
  } = serviceConfig;

  const emit = (patch) => onChange && onChange(patch);

  const buildBotUrl = () => {
    const url = new URL(`${FRONTEND_URL}/bot`);
    if (botRef) url.searchParams.set("ref", botRef);

    return url.toString();
  };

  const handleOpenChatbot = () => {
    const href = buildBotUrl();
    if (!botRef) {
      message.warning("Bot reference not found");
      return;
    }
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const handleCopyLink = async () => {
    const href = buildBotUrl();
    try {
      await navigator.clipboard.writeText(href);
      message.success("Link copied to clipboard!");
    } catch (err) {
      console.error("Copy failed:", err);
      message.error("Failed to copy link.");
    }
  };

  const handleDownload = async (qrCodeImage) => {
    if (!qrCodeImage) {
      message.warning("File not found");
      return;
    }
    const downloadMessage = message.loading("Downloading", 0);
    try {
      const response = await axios({
        url: `${BACKEND_URL}api/upload/qrCode?fileName=${encodeURIComponent(
          qrCodeImage
        )}`,
        method: "GET",
        responseType: "blob",
      });
      // Extract the file name from the url
      const extractedFileName = qrCodeImage.split("/").pop();

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", extractedFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success("Downloaded!");
    } catch (error) {
      message.error("Error downloading the file");
      console.error("Error downloading the file:", error);
    } finally {
      downloadMessage();
    }
  };

  const handleUpdate = () => {
    onSave && onSave();
  };

  const handleCreateQrCode = () => {
    if (openQRCodeModal) {
      openQRCodeModal(serviceConfig); // send full config upwards
    }
  };

  const triggerUpload = () => fileInputRef.current?.click();
  const handleFileChange = (e) => onUpload && onUpload(e, serviceKey);

  const styles = {
    shadow: {
      boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
    },
  };

  return (
    <>
      <EditUrlModal
        show={urlEditModal}
        botRef={botRef}
        onHide={() => setUrlEditModal(false)}
        oldUrl={`${FRONTEND_URL}/bot?ref=${botRef}`}
        onSave={(newUrl) => {
          console.log("✅ New URL saved:", newUrl);
        }}
      />

      <Card
        style={editing ? styles.shadow : {}}
        className={`transition-card ${editing ? "editing" : ""}`}
      >
        <Card.Header className="pb-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              {/* <QrCode className="text-primary" size={24} /> */}
              <Card.Title className="mb-0 text-primary">{title}</Card.Title>
            </div>
            {editing ? (
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setEditing(!editing)}
                  className="d-flex align-items-center gap-1"
                >
                  Close
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleUpdate}
                  className="d-flex align-items-center gap-1"
                >
                  <Check size={16} />
                  Save
                </Button>
              </div>
            ) : (
              <Button
                variant="outline-primary"
                className="border-0"
                size="sm"
                onClick={() => setEditing(!editing)}
              >
                <Edit size={16} />
              </Button>
            )}
          </div>
        </Card.Header>

        <Card.Body>
          <Row className="g-4 justify-content-between align-items-center">
            {/* Left Side - Controls and Settings */}
            <Col lg={8}>
              {/* Chatbot  link */}
              <div>
                {editing && (
                  <Form.Label className="text-secondary mb-2">
                    Chatbot URL
                  </Form.Label>
                )}
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Enter chatbot URL"
                    value={`${FRONTEND_URL}/bot?ref=${botRef}`}
                    disabled
                    className="form-control bg-white text-secondary fw-light"
                  />
                  <Button
                    variant="outline-secondary border-1 border"
                    onClick={handleOpenChatbot}
                  >
                    <ExternalLink size={16} />
                  </Button>
                  <Button
                    variant="outline-secondary border-1 border"
                    onClick={handleCopyLink}
                  >
                    <Copy size={16} />
                  </Button>
                </InputGroup>
              </div>

              {/* Flow Type Selection */}

              {editing ? (
                <div className="my-3">
                  <Form.Label className="text-secondary mb-2">
                    Flow Type
                  </Form.Label>
                  <Form.Select
                    value={flow || ""}
                    onChange={(e) => emit({ flow: e.target.value })}
                  >
                    {filteredFlowTypes.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ) : (
                <div className="my-2">
                  <div className="border rounded p-2">
                    <span className="text-primary">Type: </span>
                    <span className="text-muted fw-light">
                      {flowTypes.find((f) => f.value === flow)?.label ||
                        "Not selected"}
                    </span>
                  </div>
                </div>
              )}
              {/* Processing Fee Toggle */}

              <div className="p-3 border border-1 rounded-2 mb-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <Form.Label className="fw-medium text-secondary mb-1">
                      Processing Fee
                    </Form.Label>
                    <div className="small text-muted">
                      Allow guest to choose to pay the processing fee.
                    </div>
                  </div>
                  <Form.Check
                    type="switch"
                    id={`${title}-fee`}
                    checked={!!feeOption}
                    onChange={(e) => {
                      editing && emit({ feeOption: e.target.checked });
                    }}
                  />
                </div>
              </div>
            </Col>

            {/* Right Side - QR Code Display */}
            <Col
              md={4}
              className="d-flex justify-content-center align-items-center"
            >
              <div className="">
                <img
                  style={{ width: "170px", height: "170px" }}
                  src={qrCode || PLACE_HOLDER_IMAGE}
                  alt={`QR Code for ${title}`}
                  className="d-block mx-auto"
                />

                {editing && (
                  <div className="mt-3 mb-2 d-flex justify-content-center gap-2">
                    <Button
                      variant="outline-primary"
                      onClick={handleCreateQrCode}
                      className="d-flex align-items-center gap-1"
                    >
                      <Plus size={16} />
                      Create
                    </Button>
                    {/* Hidden input for upload */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,application/pdf"
                      className="d-none"
                      onChange={handleFileChange}
                    />

                    <Button
                      variant="outline-primary"
                      onClick={triggerUpload}
                      className="d-flex align-items-center gap-1"
                      disabled={uploading}
                    >
                      {uploading ? (
                        <Loader size="sm" />
                      ) : (
                        <RefreshCwIcon size={16} />
                      )}
                      {uploading ? "Uploading..." : "Upload"}
                    </Button>
                    <Button
                      variant="outline-info"
                      className="d-flex align-items-center gap-1"
                      onClick={() => setUrlEditModal(true)}
                    >
                      <Link size={14} />
                    </Button>
                    <Button
                      variant="outline-success"
                      className="d-flex align-items-center gap-1"
                      onClick={() => handleDownload(qrCode)}
                    >
                      <Download size={14} />
                    </Button>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default QrCodeDetail;
